import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EmptyState from "@/components/EmptyState";
import { CalendarBlank, FilePdf, FileXls, CaretRight, CircleNotch } from "@phosphor-icons/react";
import PageShortcutHeader from "@/components/PageShortcutHeader";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n || 0));
const fmtSigned = (n) => {
    const v = Number(n || 0);
    const abs = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Math.abs(v));
    return v >= 0 ? `${abs} Dr` : `${abs} Cr`;
};
// v1.29.67 — Compact Indian currency for MOBILE tables (₹1.65Cr / ₹5L / ₹8.2K)
// so all columns fit without horizontal swiping. Desktop keeps the full value.
const compactINR = (n) => {
    const abs = Math.abs(Number(n) || 0);
    if (abs >= 1e7) return `₹${(abs / 1e7).toFixed(2).replace(/\.?0+$/, "")}Cr`;
    if (abs >= 1e5) return `₹${(abs / 1e5).toFixed(2).replace(/\.?0+$/, "")}L`;
    if (abs >= 1e3) return `₹${(abs / 1e3).toFixed(1).replace(/\.?0+$/, "")}K`;
    return `₹${abs.toLocaleString("en-IN")}`;
};
function Amt({ value, signed = false }) {
    const num = Number(value) || 0;
    const full = signed ? fmtSigned(num) : fmt(num);
    const short = signed ? `${compactINR(num)} ${num >= 0 ? "Dr" : "Cr"}` : compactINR(num);
    return (<><span className="md:hidden">{short}</span><span className="hidden md:inline">{full}</span></>);
}
const dmY = (d) => {
    if (!d) return "—";
    const [y, m, day] = String(d).slice(0, 10).split("-");
    return day ? `${day}-${m}-${y}` : d;
};
const thisMonth = () => new Date().toISOString().slice(0, 7);
const monthsAgo = (n) => { const d = new Date(); d.setMonth(d.getMonth() - n); return d.toISOString().slice(0, 7); };

// ---- Client-side export helpers (no backend dependency) ----
const buildHTML = (title, headers, rows) => {
    const th = headers.map((h) => `<th style="border:1px solid #999;padding:6px;background:#065f46;color:#fff;text-align:left">${h}</th>`).join("");
    const trs = rows.map((r) => `<tr>${r.map((c, i) => `<td style="border:1px solid #ccc;padding:6px;${i === 0 ? "" : "text-align:right"}">${c}</td>`).join("")}</tr>`).join("");
    return `<h2 style="font-family:sans-serif">${title}</h2><table style="border-collapse:collapse;font-family:sans-serif;font-size:13px">${`<tr>${th}</tr>`}${trs}</table>`;
};
const exportExcel = (title, headers, rows, filename) => {
    const html = `<html><head><meta charset="utf-8"></head><body>${buildHTML(title, headers, rows)}</body></html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${filename}.xls`; a.click();
    URL.revokeObjectURL(url);
};
const exportPDF = (title, headers, rows) => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to export PDF"); return; }
    w.document.write(`<html><head><title>${title}</title></head><body onload="window.print()">${buildHTML(title, headers, rows)}</body></html>`);
    w.document.close();
};

export default function MonthlySummary() {
    const [mode, setMode] = useState("range"); // "range" | "party"
    const [parties, setParties] = useState([]);
    const [partyId, setPartyId] = useState("all");
    const [from, setFrom] = useState(monthsAgo(5));
    const [to, setTo] = useState(thisMonth());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState({});
    const [drill, setDrill] = useState(null); // v1.29.55 — { partyId, partyName, range, count, txns }
    const [error, setError] = useState("");
    const [visible, setVisible] = useState(25);        // v1.29.59 — paginate main table (25/page)
    const [drillVisible, setDrillVisible] = useState(25); // v1.29.59 — paginate drill panel (25/page)
    const [monthVisible, setMonthVisible] = useState({}); // v1.29.59 — per-month accordion pagination
    const navigate = useNavigate();

    // v1.29.55 — open a transaction's full Slip Verification detail (or its
    // month in the party ledger when it has no linked slip).
    const openTxn = (t, pid) => {
        if (t.upload_id) navigate(`/admin/uploads?upload_id=${t.upload_id}`);
        else navigate(`/admin/reports?partyId=${pid}&date_from=${t.date}&date_to=${t.date}`);
    };

    // v1.29.55 — Mode 1 drill-down: clicking a party row/amount slides in a
    // panel listing every transaction behind that party's range totals.
    const openPartyDrill = async (p) => {
        setDrillVisible(25);
        try {
            const { data: d } = await api.get("/reports/monthly-summary", { params: { date_from: from, date_to: to, party_id: p.party_id }, timeout: 10000 });
            const txns = (d.months || []).flatMap((m) => m.transactions || []);
            setDrill({ partyId: p.party_id, partyName: p.party_name, range: `${d.date_from} → ${d.date_to}`, count: txns.length, txns });
        } catch (e) {
            toast.error(e?.code === "ECONNABORTED" ? "Report too large — please narrow your date range" : formatApiError(e));
        }
    };

    useEffect(() => {
        api.get("/parties").then((r) => setParties(r.data)).catch((e) => toast.error(formatApiError(e)));
    }, []);

    // Mode 2 forces a specific party (no ALL option).
    useEffect(() => {
        if (mode === "party" && (partyId === "all" || !partyId)) setPartyId("");
        if (mode === "range" && !partyId) setPartyId("all");
        setData(null);
    }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

    const run = () => {
        if (from > to) { toast.error("From month is after To month"); return; }
        if (mode === "party" && (!partyId || partyId === "all")) { toast.error("Select a party for this mode"); return; }
        setLoading(true); setExpanded({}); setMonthVisible({}); setError(""); setVisible(25); setData(null);
        api.get("/reports/monthly-summary", { params: { date_from: from, date_to: to, party_id: mode === "party" ? partyId : partyId }, timeout: 10000 })
            .then((r) => setData(r.data))
            .catch((e) => {
                setData(null);
                const msg = e?.code === "ECONNABORTED"
                    ? "Report too large — please narrow your date range"
                    : formatApiError(e);
                setError(msg); toast.error(msg);
            })
            .finally(() => setLoading(false));
    };

    const rangeLabel = `${dmY(from + "-01")} → ${to}`;
    const isAll = data?.mode === "all";

    const doExport = (kind) => {
        if (!data) return;
        if (isAll) {
            const headers = ["Party", "UTRs", "Total Recd", "Total Paid", "Net", "Pending"];
            const rows = data.parties.map((p) => [p.party_name, p.utr_count, fmt(p.total_recd), fmt(p.total_paid), fmtSigned(p.net), p.pending_count]);
            rows.push(["GRAND TOTAL", data.grand_total.utr_count, fmt(data.grand_total.total_recd), fmt(data.grand_total.total_paid), fmtSigned(data.grand_total.net), data.grand_total.pending_count]);
            const title = `Monthly Summary · All Parties · ${data.date_from} to ${data.date_to}`;
            kind === "pdf" ? exportPDF(title, headers, rows) : exportExcel(title, headers, rows, "monthly-summary-all-parties");
        } else {
            const headers = ["Month", "UTRs", "Total Recd", "Total Paid", "Net", "Running Balance"];
            const rows = data.months.map((m) => [m.month_label, m.utr_count, fmt(m.total_recd), fmt(m.total_paid), fmtSigned(m.net), fmtSigned(m.running_balance)]);
            rows.push(["TOTALS", data.totals.utr_count, fmt(data.totals.total_recd), fmt(data.totals.total_paid), fmtSigned(data.totals.net), ""]);
            const title = `Monthly Summary · ${data.party?.name || ""} · ${data.date_from} to ${data.date_to}`;
            kind === "pdf" ? exportPDF(title, headers, rows) : exportExcel(title, headers, rows, "monthly-summary-party");
        }
    };

    return (
        <div className="space-y-2 md:space-y-6" data-testid="monthly-summary-page">
            <PageShortcutHeader
                action_id="page:monthly-summary"
                label="Monthly Summary"
                target_route="/admin/monthly-summary"
                eyebrow="Statements"
                title="Monthly Summary"
                titleClassName="mt-1 font-display text-lg font-bold tracking-tight text-slate-900 md:text-4xl"
                subtitle="Two ways to slice your ledger: all-party totals for a date range, or a single party's month-by-month breakdown."
                subtitleClassName="mt-2 hidden text-sm text-slate-500 md:block"
            />

            {/* Mode toggle */}
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm" data-testid="ms-mode-toggle">
                <button
                    type="button"
                    data-testid="ms-mode-range"
                    onClick={() => setMode("range")}
                    className={`rounded-md px-2.5 py-1.5 text-xs font-semibold transition md:px-4 md:text-sm ${mode === "range" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                >
                    Date Range + Party
                </button>
                <button
                    type="button"
                    data-testid="ms-mode-party"
                    onClick={() => setMode("party")}
                    className={`rounded-md px-2.5 py-1.5 text-xs font-semibold transition md:px-4 md:text-sm ${mode === "party" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                >
                    Single Party (month-wise)
                </button>
            </div>

            {/* Controls */}
            <div className="grid grid-cols-2 gap-2 rounded-lg border border-slate-200 bg-white p-2 shadow-sm md:grid-cols-4 md:gap-4 md:p-4">
                <div className="min-w-0">
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 md:text-xs">From (Month/Year)</Label>
                    <input type="month" data-testid="ms-from" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 w-full min-w-0 max-w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
                </div>
                <div className="min-w-0">
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 md:text-xs">To (Month/Year)</Label>
                    <input type="month" data-testid="ms-to" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 w-full min-w-0 max-w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 md:text-xs">Party</Label>
                    <Select value={partyId} onValueChange={setPartyId}>
                        <SelectTrigger data-testid="ms-party-select" className="mt-1 h-9 md:h-10">
                            <SelectValue placeholder="Select party" />
                        </SelectTrigger>
                        <SelectContent>
                            {mode === "range" && <SelectItem value="all" data-testid="ms-party-all">ALL PARTIES</SelectItem>}
                            {parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="col-span-2 flex items-end md:col-span-1">
                    <button type="button" data-testid="ms-run" onClick={run} className="h-10 w-full rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 md:h-auto">
                        {loading ? "Loading…" : "Generate Report"}
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-slate-200 bg-white py-16" data-testid="ms-loading">
                    <CircleNotch size={34} weight="bold" className="animate-spin text-emerald-600" />
                    <div className="text-sm font-medium text-slate-600">Generating report… the page stays responsive.</div>
                </div>
            ) : !data ? (
                <EmptyState icon={CalendarBlank} title={error ? "Report failed" : "Generate a report"} description={error ? error : "Pick your mode, date range and party, then Generate Report."} />
            ) : (
                <>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="text-sm text-slate-500">Range: <span className="font-semibold text-slate-800">{data.date_from} → {data.date_to}</span> {!isAll && data.party ? `· ${data.party.name}` : "· All parties"}</div>
                        <div className="flex gap-2">
                            <button type="button" data-testid="ms-export-pdf" onClick={() => doExport("pdf")} className="inline-flex items-center gap-1.5 rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm font-semibold text-rose-700 hover:bg-rose-100">
                                <FilePdf size={16} weight="fill" /> Export PDF
                            </button>
                            <button type="button" data-testid="ms-export-excel" onClick={() => doExport("excel")} className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100">
                                <FileXls size={16} weight="fill" /> Export Excel
                            </button>
                        </div>
                    </div>

                    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                        <div className="overflow-x-auto">
                            {isAll ? (
                                <table className="w-full text-sm" data-testid="ms-all-table">
                                    <thead>
                                        <tr className="bg-emerald-700 text-left text-[10px] font-semibold uppercase tracking-wider text-white">
                                            <th className="px-4 py-3">Party</th>
                                            <th className="px-4 py-3 text-right">UTRs Recd</th>
                                            <th className="px-4 py-3 text-right">Total Recd</th>
                                            <th className="px-4 py-3 text-right">Total Paid</th>
                                            <th className="px-4 py-3 text-right">Net Balance</th>
                                            <th className="px-4 py-3 text-right">Pending Slips</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.parties.length === 0 && (
                                            <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No activity in this range.</td></tr>
                                        )}
                                        {data.parties.slice(0, visible).map((p) => (
                                            <tr key={p.party_id} data-testid={`ms-all-row-${p.party_id}`} onClick={() => openPartyDrill(p)} title="Click to see the transactions behind these totals" className="cursor-pointer border-t border-slate-100 hover:bg-blue-50/60">
                                                <td className="px-4 py-2.5 font-semibold text-blue-700 underline decoration-dotted">{p.party_name}</td>
                                                <td className="px-4 py-2.5 text-right tabular-nums">{p.utr_count}</td>
                                                <td className="px-4 py-2.5 text-right tabular-nums text-emerald-700"><Amt value={p.total_recd} /></td>
                                                <td className="px-4 py-2.5 text-right tabular-nums text-violet-700"><Amt value={p.total_paid} /></td>
                                                <td className="px-4 py-2.5 text-right tabular-nums font-semibold"><Amt value={p.net} signed /></td>
                                                <td className="px-4 py-2.5 text-right tabular-nums">{p.pending_count > 0 ? <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">{p.pending_count}</span> : <span className="text-slate-300">0</span>}</td>
                                            </tr>
                                        ))}
                                        {data.parties.length > visible && (
                                            <tr data-testid="ms-load-more-row"><td colSpan={6} className="px-4 py-3 text-center">
                                                <button type="button" data-testid="ms-load-more" onClick={() => setVisible((v) => v + 25)} className="rounded-md border border-slate-300 bg-white px-4 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-100">Load more ({data.parties.length - visible} left)</button>
                                            </td></tr>
                                        )}
                                        <tr data-testid="ms-grand-total" className="border-t-2 border-emerald-700 bg-slate-50 font-bold">
                                            <td className="px-4 py-3 uppercase tracking-wider text-[11px] text-slate-700">Grand Total · {data.parties.length} parties</td>
                                            <td className="px-4 py-3 text-right tabular-nums">{data.grand_total.utr_count}</td>
                                            <td className="px-4 py-3 text-right tabular-nums text-emerald-700"><Amt value={data.grand_total.total_recd} /></td>
                                            <td className="px-4 py-3 text-right tabular-nums text-violet-700"><Amt value={data.grand_total.total_paid} /></td>
                                            <td className="px-4 py-3 text-right tabular-nums"><Amt value={data.grand_total.net} signed /></td>
                                            <td className="px-4 py-3 text-right tabular-nums">{data.grand_total.pending_count}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            ) : (
                                <table className="w-full text-sm" data-testid="ms-party-table">
                                    <thead>
                                        <tr className="bg-emerald-700 text-left text-[10px] font-semibold uppercase tracking-wider text-white">
                                            <th className="px-4 py-3 w-8"></th>
                                            <th className="px-4 py-3">Month</th>
                                            <th className="px-4 py-3 text-right">UTRs</th>
                                            <th className="px-4 py-3 text-right">Total Recd</th>
                                            <th className="px-4 py-3 text-right">Total Paid</th>
                                            <th className="px-4 py-3 text-right">Net</th>
                                            <th className="px-4 py-3 text-right">Running Balance</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.months.length === 0 && (
                                            <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No transactions in this range.</td></tr>
                                        )}
                                        {data.months.map((m) => (
                                            <React.Fragment key={m.month}>
                                                <tr
                                                    data-testid={`ms-month-row-${m.month}`}
                                                    onClick={() => setExpanded((e) => ({ ...e, [m.month]: !e[m.month] }))}
                                                    className="cursor-pointer border-t border-slate-100 hover:bg-blue-50/60"
                                                >
                                                    <td className="px-4 py-2.5 text-slate-400">
                                                        <CaretRight size={14} weight="bold" className={`transition-transform ${expanded[m.month] ? "rotate-90" : ""}`} />
                                                    </td>
                                                    <td className="px-4 py-2.5 font-semibold text-slate-900">{m.month_label}</td>
                                                    <td className="px-4 py-2.5 text-right tabular-nums">{m.utr_count}</td>
                                                    <td className="px-4 py-2.5 text-right tabular-nums text-emerald-700"><Amt value={m.total_recd} /></td>
                                                    <td className="px-4 py-2.5 text-right tabular-nums text-violet-700"><Amt value={m.total_paid} /></td>
                                                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold"><Amt value={m.net} signed /></td>
                                                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-slate-900"><Amt value={m.running_balance} signed /></td>
                                                </tr>
                                                {expanded[m.month] && (() => {
                                                    const mv = monthVisible[m.month] || 25;
                                                    return (
                                                    <tr data-testid={`ms-month-detail-${m.month}`}>
                                                        <td colSpan={7} className="bg-slate-50 px-4 py-2">
                                                            <table className="w-full text-xs">
                                                                <thead>
                                                                    <tr className="text-left text-slate-400">
                                                                        <th className="py-1">Date</th><th className="py-1">UTR / Ref</th><th className="py-1">Mode</th><th className="py-1 text-right">Amount</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {m.transactions.slice(0, mv).map((t, i) => (
                                                                        <tr key={i} data-testid={`ms-txn-${m.month}-${i}`} onClick={() => openTxn(t, partyId)} title="Open slip verification detail" className="cursor-pointer border-t border-slate-200/70 hover:bg-blue-50">
                                                                            <td className="py-1 text-slate-600">{dmY(t.date)}</td>
                                                                            <td className="py-1 font-mono text-slate-700">{t.utr || "—"}</td>
                                                                            <td className="py-1 uppercase text-slate-500">{t.txn_type === "payment" ? "Recd" : t.txn_type === "payout" ? "Paid" : t.mode}</td>
                                                                            <td className={`py-1 text-right tabular-nums ${t.txn_type === "payment" ? "text-emerald-700" : t.txn_type === "payout" ? "text-violet-700" : "text-slate-600"}`}>{fmt(t.amount)}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                            {m.transactions.length > mv && (
                                                                <div className="mt-2 text-center">
                                                                    <button type="button" data-testid={`ms-month-load-more-${m.month}`} onClick={() => setMonthVisible((s) => ({ ...s, [m.month]: mv + 25 }))} className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100">Load more ({m.transactions.length - mv} left)</button>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                    );
                                                })()}
                                            </React.Fragment>
                                        ))}
                                        <tr data-testid="ms-party-total" className="border-t-2 border-emerald-700 bg-slate-50 font-bold">
                                            <td className="px-4 py-3" />
                                            <td className="px-4 py-3 uppercase tracking-wider text-[11px] text-slate-700">Totals · {data.months.length} months</td>
                                            <td className="px-4 py-3 text-right tabular-nums">{data.totals.utr_count}</td>
                                            <td className="px-4 py-3 text-right tabular-nums text-emerald-700"><Amt value={data.totals.total_recd} /></td>
                                            <td className="px-4 py-3 text-right tabular-nums text-violet-700"><Amt value={data.totals.total_paid} /></td>
                                            <td className="px-4 py-3 text-right tabular-nums"><Amt value={data.totals.net} signed /></td>
                                            <td className="px-4 py-3 text-right tabular-nums text-slate-400">Pending: {data.totals.pending_count}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                    {!isAll && <p className="text-center text-xs italic text-slate-500">Click any month row to expand its individual transactions.</p>}
                </>
            )}

            {/* v1.29.55 — Drill-down slide-in panel (Mode 1). Lists every txn
                behind a party's range totals; each row opens Slip Verification.
                Back / outside-click closes without losing the report state. */}
            {drill && (
                <div className="fixed inset-0 z-[60] flex justify-end bg-black/40" data-testid="ms-drill-panel" onClick={() => setDrill(null)}>
                    <div className="flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <div className="shrink-0 border-b border-slate-200 bg-white px-5 py-3">
                            <div className="flex items-center justify-between">
                                <div className="text-xs text-slate-500" data-testid="ms-drill-breadcrumb">
                                    Monthly Summary <span className="text-slate-300">→</span> <b className="text-slate-800">{drill.partyName}</b> <span className="text-slate-300">→</span> {drill.range} <span className="text-slate-300">→</span> {drill.count} transactions
                                </div>
                                <button type="button" data-testid="ms-drill-back" onClick={() => setDrill(null)} className="rounded-md border border-slate-200 px-3 py-1 text-sm font-semibold text-slate-700 hover:bg-slate-100">← Back</button>
                            </div>
                        </div>
                        <div className="ms-drill-scroll min-h-0 flex-1 overflow-y-auto overflow-x-auto p-4">
                            <table className="w-full text-xs" data-testid="ms-drill-table">
                                <thead>
                                    <tr className="bg-slate-800 text-left uppercase tracking-wider text-white">
                                        <th className="px-2 py-2">Date</th><th className="px-2 py-2">UTR</th><th className="px-2 py-2">Mode</th>
                                        <th className="px-2 py-2 text-right">Recd</th><th className="px-2 py-2 text-right">Paid</th><th className="px-2 py-2">Remark</th><th className="px-2 py-2">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {drill.txns.length === 0 && <tr><td colSpan={7} className="px-2 py-6 text-center text-slate-400">No transactions.</td></tr>}
                                    {drill.txns.slice(0, drillVisible).map((t, i) => (
                                        <tr key={i} data-testid={`ms-drill-txn-${i}`} onClick={() => openTxn(t, drill.partyId)} title="Open slip verification detail" className="cursor-pointer border-t border-slate-100 hover:bg-blue-50">
                                            <td className="px-2 py-1.5 text-slate-600">{dmY(t.date)}</td>
                                            <td className="px-2 py-1.5 font-mono text-slate-700">{t.utr || "—"}</td>
                                            <td className="px-2 py-1.5 uppercase text-slate-500">{t.mode}</td>
                                            <td className="px-2 py-1.5 text-right tabular-nums text-emerald-700">{t.txn_type === "payment" ? fmt(t.amount) : "—"}</td>
                                            <td className="px-2 py-1.5 text-right tabular-nums text-violet-700">{t.txn_type === "payout" ? fmt(t.amount) : "—"}</td>
                                            <td className="px-2 py-1.5 text-slate-600">{t.remark || "—"}</td>
                                            <td className="px-2 py-1.5"><span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-600">{t.status}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {drill.txns.length > drillVisible && (
                                <div className="mt-3 text-center">
                                    <button type="button" data-testid="ms-drill-load-more" onClick={() => setDrillVisible((v) => v + 25)} className="rounded-md border border-slate-300 bg-white px-4 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-100">Load more ({drill.txns.length - drillVisible} left)</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
